import moment from "moment";
import queryString from "query-string";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFormsySubmissionsForClient,
  getSubmission,
  handleCopyUrl,
  handleFormChange,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScripts";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
  getFieldValue,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import {
  Checkbox,
  Input,
  RadioButton,
  Select,
  TextArea,
} from "../common/ControlBundle";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import { getSchema } from "./joi/validation";

function INTFTCParentCoaching() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const yesNoOptions = [
    {
      _id: "1",
      name: "Yes",
    },
    { _id: "0", name: "No" },
  ];

  const problemBehaviorOptions = [
    { _id: "1", name: "Argues" },
    { _id: "2", name: "Shows poor self-hygiene" },
    { _id: "3", name: "Fights/physically aggressive" },
    { _id: "4", name: "Does not complete homework" },
    { _id: "5", name: "Dishonest" },
    { _id: "6", name: "Does not complete chores" },
    { _id: "7", name: "Does not follow directions" },
    { _id: "8", name: "Steals" },
    { _id: "9", name: "Does not follow house rules" },
    { _id: "10", name: "Has school-related issues" },
    { _id: "11", name: "Sexually inappropriate" },
    { _id: "12", name: "Disrespectful/talks back" },
    { _id: "13", name: "Other" },
  ];

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="clearfix d-print-none">
                <div className="float-end">
                  <button
                    className="btn btn-link"
                    title="Copy and Open Encounter"
                    onClick={() => {
                      const encounterEnvironment =
                        process.env.REACT_APP_ENVIRONMENT === "Production"
                          ? "ccplive-cp"
                          : "d365base-cp";
                      handleCopyUrl(location.pathname).then(() => {
                        window.open(
                          `https://${encounterEnvironment}.safy.org/dist/EncounterPortal/index.html?contactid=%7B${clientid}%7D`
                        );
                      });
                    }}
                  >
                    Copy form link and launch to Encounter
                  </button>
                </div>
              </div>
              <div className="container">
                <div className="row">
                  <div className="col">
                    <Input
                      name="CoachName"
                      label="Coach Name:"
                      error={errors.CoachName}
                      value={data.CoachName["@value"]}
                      onChange={handleChange}
                      identical={compare("CoachName")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Input
                      name="DateOfSession"
                      label="Date:"
                      type="date"
                      error={errors.DateOfSession}
                      value={data.DateOfSession["@value"]}
                      identical={compare("DateOfSession")}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Input
                      name="FosterHomeName"
                      label="Foster Home Name:"
                      error={errors.FosterHomeName}
                      value={data.FosterHomeName["@value"]}
                      onChange={handleChange}
                      identical={compare("FosterHomeName")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Input
                      name="Agency"
                      label="Agency:"
                      error={errors.Agency}
                      value={data.Agency["@value"]}
                      onChange={handleChange}
                      identical={compare("Agency")}
                      commentsHandler={handleComments}
                      disabled
                    />
                  </div>
                </div>
              </div>
              <u>Section I: Positive Behaviors</u>
              <br />
              <b>
                Which <u>two</u> positive behaviors have you observed with your
                child in the past two weeks?
              </b>
              <div className="container">
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="FollowsDirections"
                      label="Follows instructions/directions"
                      checked={data.FollowsDirections["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="CompletesHomework"
                      label="Completes homework"
                      checked={data.CompletesHomework["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="FollowsRules"
                      label="Follows house rules"
                      checked={data.FollowsRules["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="GoodHygiene"
                      label="Good self-hygiene"
                      checked={data.GoodHygiene["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="AppropriateTone"
                      label="Uses appropriate language/tone"
                      checked={data.AppropriateTone["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="TellsTruth"
                      label="Tells the truth"
                      checked={data.TellsTruth["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="PerformsWell"
                      label="Performs well in school"
                      checked={data.PerformsWell["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="RespectsProperty"
                      label="Respects others’ property"
                      checked={data.RespectsProperty["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="CompletesChores"
                      label="Completes chores"
                      checked={data.CompletesChores["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-4">
                    <Checkbox
                      name="RespectsBoundaries"
                      label="Respects boundaries"
                      checked={data.RespectsBoundaries["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ExpressesFeelings"
                      label="Expresses feelings/thoughts appropriately"
                      checked={data.ExpressesFeelings["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-4">
                    <Checkbox
                      name="OtherPositive"
                      label="Other (keep it behavioral - what it looks/sounds like)"
                      checked={data.OtherPositive["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    {data.OtherPositive["@value"] === "true" && (
                      <Input
                        name="OtherPositiveDetails"
                        error={errors.OtherPositiveDetails}
                        value={data.OtherPositiveDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("OtherPositiveDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                </div>
              </div>
              <b>
                What <u>two</u> interventions/strategies did you use to
                reinforce positive behaviors?
              </b>
              <div className="container">
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="VerbalPraise"
                      label="Verbal praise"
                      checked={data.VerbalPraise["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="NonVerbalPraise"
                      label="Non-verbal praise (high 5, thumbs up, hug)"
                      checked={data.NonVerbalPraise["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="ReminderOfReward"
                      label="Reminder of possible reward"
                      checked={data.ReminderOfReward["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="BehaviorContract"
                      label="Behavior contract"
                      checked={data.BehaviorContract["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="NaturalConsequence"
                      label="Natural consequence"
                      checked={data.NaturalConsequence["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="Reward"
                      label="Reward/incentive/extra privilege"
                      checked={data.Reward["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="TrackPositiveBehavior"
                      label="Track positive behavior"
                      checked={data.TrackPositiveBehavior["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="OtherStrategy"
                      label="Other (keep it behavioral - what it looks/sounds like)"
                      checked={data.OtherStrategy["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                {data.OtherStrategy["@value"] === "true" && (
                  <div className="row">
                    <div className="col"></div>
                    <div className="col">
                      <Input
                        name="OtherStrategyDetails"
                        error={errors.OtherStrategyDetails}
                        value={data.OtherStrategyDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("OtherStrategyDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                )}
              </div>
              <RadioButton
                name="StrategiesWorking"
                label="Are the interventions/strategies used to reinforce positive behaviors working?"
                options={yesNoOptions}
                error={errors.StrategiesWorking}
                value={data.StrategiesWorking["@value"]}
                onChange={handleChange}
                identical={compare("StrategiesWorking")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.StrategiesWorking["@value"] === "0" && (
                <Input
                  name="StrategiesToTry"
                  label="If not, what do you want to try in the next two weeks?"
                  error={errors.StrategiesToTry}
                  value={data.StrategiesToTry["@value"]}
                  onChange={handleChange}
                  identical={compare("StrategiesToTry")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              )}
              <u>Section II: Problematic Behaviors</u>
              <Input
                name="ProblematicBehaviors"
                label="What problematic behaviors have you observed with your child in the past two weeks? (List two, see drop box below)"
                error={errors.ProblematicBehaviors}
                value={data.ProblematicBehaviors["@value"]}
                onChange={handleChange}
                identical={compare("ProblematicBehaviors")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <b>
                Provide details below for the main problematic behavior you
                would like to focus on in the next two weeks:
              </b>
              <div className="container">
                <div className="row border-bottom mb-2">
                  <div className="col"></div>
                  <div className="col">
                    <b>Problem Behavior</b>
                    <Select
                      name="ProblemBehavior"
                      options={problemBehaviorOptions}
                      error={errors.ProblemBehavior}
                      value={data.ProblemBehavior["@value"]}
                      onChange={handleChange}
                      identical={compare("ProblemBehavior")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.ProblemBehavior["@value"] === "13" && (
                      <Input
                        name="ProblemBehaviorDetails"
                        error={errors.ProblemBehaviorDetails}
                        value={data.ProblemBehaviorDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("ProblemBehaviorDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                </div>
                <div className="row border-bottom mb-2">
                  <div className="col">
                    <b>FREQUENCY</b> (How many times per day/week is it
                    occurring?)
                  </div>
                  <div className="col">
                    <Input
                      name="BehaviorFrequency"
                      error={errors.BehaviorFrequency}
                      value={data.BehaviorFrequency["@value"]}
                      onChange={handleChange}
                      identical={compare("BehaviorFrequency")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom mb-2">
                  <div className="col">
                    <b>DURATION</b> (How long did the behavior last?)
                  </div>
                  <div className="col">
                    <Input
                      name="BehaviorDuration"
                      error={errors.BehaviorDuration}
                      value={data.BehaviorDuration["@value"]}
                      onChange={handleChange}
                      identical={compare("BehaviorDuration")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom mb-2">
                  <div className="col">
                    <b>INTENSITY</b> (if applicable, i.e. aggression)
                  </div>
                  <div className="col">
                    <Input
                      name="BehaviorIntensity"
                      error={errors.BehaviorIntensity}
                      value={data.BehaviorIntensity["@value"]}
                      onChange={handleChange}
                      identical={compare("BehaviorIntensity")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom mb-2">
                  <div className="col">
                    <b>ABCs – ANTECEDENT</b> (what did you notice before the
                    behavior?)
                  </div>
                  <div className="col">
                    <Input
                      name="BehaviorAntecedent"
                      error={errors.BehaviorAntecedent}
                      value={data.BehaviorAntecedent["@value"]}
                      onChange={handleChange}
                      identical={compare("BehaviorAntecedent")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom mb-2">
                  <div className="col">
                    <b>ABCs – CONSEQUENCE</b> (what intervention did you use?)
                  </div>
                  <div className="col">
                    <Input
                      name="BehaviorConsequence"
                      error={errors.BehaviorConsequence}
                      value={data.BehaviorConsequence["@value"]}
                      onChange={handleChange}
                      identical={compare("BehaviorConsequence")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
              </div>
              <Input
                name="InfluenceThoughts"
                label="Problematic behaviors are often related to trauma a child has experienced, so how does the Child's “invisible suitcase” influence their thoughts about themselves, their caregivers, and the world, and lead to their problematic behaviors?"
                error={errors.InfluenceThoughts}
                value={data.InfluenceThoughts["@value"]}
                onChange={handleChange}
                identical={compare("InfluenceThoughts")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="InterventionsToAvoid"
                label="Knowing your Child's trauma history, are there any interventions that should be avoided?"
                error={errors.InterventionsToAvoid}
                value={data.InterventionsToAvoid["@value"]}
                onChange={handleChange}
                identical={compare("InterventionsToAvoid")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <b>
                What <u>two</u> interventions/strategies have you used to reduce
                or eliminate problematic behaviors?
              </b>
              <div className="container">
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="TimeIn"
                      label="Time-in"
                      checked={data.TimeIn["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="TimeOut"
                      label="Time-out"
                      checked={data.TimeOut["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="Restitution"
                      label="Restitution"
                      checked={data.Restitution["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-4">
                    <Checkbox
                      name="DirectionWithWarning"
                      label="Effective direction with 1 warning"
                      checked={data.DirectionWithWarning["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ProblemSolving"
                      label="Problem-solving steps"
                      checked={data.ProblemSolving["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-4">
                    <Checkbox
                      name="PrivilegeRemoval"
                      label="Privilege removal"
                      checked={data.PrivilegeRemoval["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="NaturalConsequenceReduce"
                      label="Natural consequence (what naturally happened without your intervention)"
                      checked={
                        data.NaturalConsequenceReduce["@value"] === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-4">
                    <Checkbox
                      name="BehaviorContractReduce"
                      label="Behavior contract"
                      checked={data.BehaviorContractReduce["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="LogicalConsequence"
                      label="Logical consequence (guiding child to take responsibility for the behavior)"
                      checked={data.LogicalConsequence["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-4">
                    <Checkbox
                      name="VerbalPraiseReduce"
                      label="Verbal praise"
                      checked={data.VerbalPraiseReduce["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="RewardReduce"
                      label="Reward/incentive for the desired behavior"
                      checked={data.RewardReduce["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="OtherReduce"
                      label="Other (keep it behavioral - what it looks/sounds like)"
                      checked={data.OtherReduce["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    {data.OtherReduce["@value"] === "true" && (
                      <Input
                        name="OtherReduceDetails"
                        error={errors.OtherReduceDetails}
                        value={data.OtherReduceDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("OtherReduceDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                </div>
              </div>
              <RadioButton
                name="StrategiesWorkingReduce"
                label="Are the interventions/strategies you have been currently using to reduce/eliminate behaviors working?"
                options={yesNoOptions}
                error={errors.StrategiesWorkingReduce}
                value={data.StrategiesWorkingReduce["@value"]}
                onChange={handleChange}
                identical={compare("StrategiesWorkingReduce")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.StrategiesWorkingReduce["@value"] === "0" && (
                <Input
                  name="NewInterventions"
                  label="If not, what new interventions do you want to try?"
                  error={errors.NewInterventions}
                  value={data.NewInterventions["@value"]}
                  onChange={handleChange}
                  identical={compare("NewInterventions")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              )}
              <u>Section III: Core Elements</u>
              <RadioButton
                name="DailyCheckIns"
                label="Are you consistently doing Daily Check-ins one-on-one with your youth (mark no if not done daily)?"
                options={yesNoOptions}
                error={errors.DailyCheckIns}
                value={data.DailyCheckIns["@value"]}
                onChange={handleChange}
                identical={compare("DailyCheckIns")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="CheckInPositiveInfo"
                label="What positive information about the child was communicated during the check-in?"
                error={errors.CheckInPositiveInfo}
                value={data.CheckInPositiveInfo["@value"]}
                onChange={handleChange}
                identical={compare("CheckInPositiveInfo")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="CheckInConcerns"
                label="What concerns/issues/goals were communicated during the check-in?"
                error={errors.CheckInConcerns}
                value={data.CheckInConcerns["@value"]}
                onChange={handleChange}
                identical={compare("CheckInConcerns")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <RadioButton
                name="FamilyMeeting"
                label="Did you hold at least one family meeting per week?"
                options={yesNoOptions}
                error={errors.FamilyMeeting}
                value={data.FamilyMeeting["@value"]}
                onChange={handleChange}
                identical={compare("FamilyMeeting")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="FamilyMeetingReviewed"
                label="List any pertinent information reviewed during the family meeting:"
                error={errors.FamilyMeetingReviewed}
                value={data.FamilyMeetingReviewed["@value"]}
                onChange={handleChange}
                identical={compare("FamilyMeetingReviewed")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <RadioButton
                name="FamilyFunTime"
                label="Did you incorporate family fun time this week?"
                options={yesNoOptions}
                error={errors.FamilyFunTime}
                value={data.FamilyFunTime["@value"]}
                onChange={handleChange}
                identical={compare("FamilyFunTime")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <RadioButton
                name="YouthIdentity"
                label="Did you participate in any discussions or activities to honor your youth’s individual identity?"
                options={yesNoOptions}
                error={errors.YouthIdentity}
                value={data.YouthIdentity["@value"]}
                onChange={handleChange}
                identical={compare("YouthIdentity")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <RadioButton
                name="PowerStruggle"
                label="Did you successfully handle a power struggle this week?"
                options={yesNoOptions}
                error={errors.PowerStruggle}
                value={data.PowerStruggle["@value"]}
                onChange={handleChange}
                identical={compare("PowerStruggle")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              What strategy/strategies did you use?
              <div className="container">
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="PreTeaching"
                      label="Used pre-teaching"
                      checked={data.PreTeaching["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="IMessage"
                      label="Used an “I” message"
                      checked={data.IMessage["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="Ignored"
                      label="Ignored (said/did nothing)"
                      checked={data.Ignored["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="Neutral"
                      label="Said something neutral"
                      checked={data.Neutral["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="NoButtons"
                      label="Did not let “buttons” get pushed"
                      checked={data.NoButtons["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="WalkedAway"
                      label="Walked away"
                      checked={data.WalkedAway["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="Concise"
                      label="Stayed short/to the point"
                      checked={data.Concise["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="RegainComposure"
                      label="Took time to regain composure"
                      checked={data.RegainComposure["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="NoArguing"
                      label="Did not argue"
                      checked={data.NoArguing["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-4">
                    <Checkbox
                      name="OtherPowerStruggleStrategy"
                      label="Other"
                      checked={
                        data.OtherPowerStruggleStrategy["@value"] === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    {data.OtherPowerStruggleStrategy["@value"] === "true" && (
                      <Input
                        name="OtherPowerStruggleStrategyDetails"
                        error={errors.OtherPowerStruggleStrategyDetails}
                        value={data.OtherPowerStruggleStrategyDetails["@value"]}
                        defaultValue={
                          data.OtherPowerStruggleStrategyDetails["@value"]
                        }
                        identical={compare("OtherPowerStruggleStrategyDetails")}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                </div>
              </div>
              <u>Section IV: Self-care</u>
              <Input
                name="FeelingToday"
                label="On a scale of 1 (not stressed) to 10 (extremely stressed), how are you feeling today:"
                error={errors.FeelingToday}
                value={data.FeelingToday["@value"]}
                onChange={handleChange}
                identical={compare("FeelingToday")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="FeelingLastMeeting"
                label="At last meeting:"
                error={errors.FeelingLastMeeting}
                value={data.FeelingLastMeeting["@value"]}
                onChange={handleChange}
                identical={compare("FeelingLastMeeting")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <b>
                What self-care activities did the parent participate in since
                our last coaching session?
              </b>
              <div className="container">
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="Respite"
                      label="Respite"
                      checked={data.Respite["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="AloneTime"
                      label="Alone time"
                      checked={data.AloneTime["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="DeepBreathing"
                      label="Deep breathing"
                      checked={data.DeepBreathing["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="Meditation"
                      label="Meditation"
                      checked={data.Meditation["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="Exercise"
                      label="Exercise"
                      checked={data.Exercise["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="Reading"
                      label="Reading"
                      checked={data.Reading["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="NightAway"
                      label="A night away"
                      checked={data.NightAway["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="Prayer"
                      label="Prayer"
                      checked={data.Prayer["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="Listening"
                      label="Listening to music"
                      checked={data.Listening["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-4">
                    <Checkbox
                      name="Hobby"
                      label="Participate in a hobby"
                      checked={data.Hobby["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="Support"
                      label="Reach out to a supportive person"
                      checked={data.Support["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="OtherSelfCare"
                      label="Other"
                      checked={data.OtherSelfCare["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    {data.OtherSelfCare["@value"] === "true" && (
                      <Input
                        name="OtherSelfCareDetails"
                        error={errors.OtherSelfCareDetails}
                        value={data.OtherSelfCareDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("OtherSelfCareDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                </div>
              </div>
              <Input
                name="SelfCareStrategies"
                label="If you have not been utilizing the strategies above, what do you plan to use?"
                error={errors.SelfCareStrategies}
                value={data.SelfCareStrategies["@value"]}
                onChange={handleChange}
                identical={compare("SelfCareStrategies")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="AdditionalComments"
                label="Additional comments:"
                rows="5"
                error={errors.AdditionalComments}
                value={data.AdditionalComments["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
                placeholder="Include foster parent response to the session and confidence in intervention/strategy implementation"
              />
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default INTFTCParentCoaching;
